import React from 'react';
import { styled } from '@linaria/react';

import { normalize, clamp } from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

interface Props {
  hue: number;
  size: number;
  saturation: number;
  lightness: number;
  setSaturation: React.Dispatch<React.SetStateAction<number>>;
  setLightness: React.Dispatch<React.SetStateAction<number>>;
}

const SatLitGrid = ({
  hue,
  size,
  saturation,
  lightness,
  setSaturation,
  setLightness,
}: Props) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isMouseDown, setIsMouseDown] = React.useState(false);

  const [mousePosition, boundingBox] = useMousePositionWithinElement(
    wrapperRef,
    {
      buffer: 128,
      throttleBy: 20,
    }
  );

  // Calculate color values from mouse position
  React.useEffect(() => {
    if (
      !isMouseDown ||
      typeof mousePosition.x !== 'number' ||
      typeof mousePosition.y !== 'number' ||
      !boundingBox
    ) {
      return;
    }

    setSaturation(
      Math.round(
        clamp(
          normalize(mousePosition.x, 0, boundingBox.width, 0, 100),
          0,
          100
        )
      )
    );
    setLightness(
      Math.round(
        clamp(
          normalize(mousePosition.y, 0, boundingBox.height, 100, 0),
          0,
          100
        )
      )
    );
  }, [
    isMouseDown,
    mousePosition,
    boundingBox,
    setSaturation,
    setLightness,
  ]);

  // Track dragging
  React.useEffect(() => {
    function handleMouseUp() {
      setIsMouseDown(false);
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isMouseDown]);

  function handleKeyDown(ev: React.KeyboardEvent<HTMLButtonElement>) {
    let satIncrement = 0;
    let litIncrement = 0;

    switch (ev.key) {
      case 'ArrowUp': {
        ev.preventDefault();
        litIncrement = 5;
        break;
      }
      case 'ArrowDown': {
        ev.preventDefault();
        litIncrement = -5;
        break;
      }
      case 'ArrowLeft': {
        ev.preventDefault();
        satIncrement = -5;
        break;
      }
      case 'ArrowRight': {
        ev.preventDefault();
        satIncrement = +5;
        break;
      }
    }

    setSaturation((sat) =>
      Math.round(clamp(sat + satIncrement, 0, 100))
    );
    setLightness((lit) =>
      Math.round(clamp(lit + litIncrement, 0, 100))
    );
  }

  const x = normalize(saturation, 0, 100, 0, size);
  const y = normalize(lightness, 0, 100, size, 0);

  return (
    <Wrapper>
      <GridWrapper
        ref={wrapperRef}
        onMouseDown={() => setIsMouseDown(true)}
        onTouchStart={() => setIsMouseDown(true)}
        style={{
          width: size,
          height: size,
          '--hue': hue + 'deg',
          cursor: isMouseDown ? 'none' : undefined,
        }}
      >
        <BackgroundGradient />
        <ColorGradient />
        <Indicator
          style={{
            '--x': `${x}px`,
            '--y': `${y}px`,
            background: `hsl(${hue}deg ${saturation}% ${lightness}%)`,
            cursor: isMouseDown ? 'none' : undefined,
          }}
          onKeyDown={handleKeyDown}
        />
      </GridWrapper>
      <SaturationAxis>
        <span>Saturation</span>
      </SaturationAxis>
      <LightnessAxis>
        <span>Lightness</span>
      </LightnessAxis>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const GridWrapper = styled.div`
  position: relative;
  border-radius: 2px;
  /* Hide corners */
  overflow: hidden;
  overflow: clip;
  border: var(--white-border-thickness) solid white;
  box-shadow:
    0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042),
    9.6px 19.2px 33.4px rgba(0, 0, 0, 0.05),
    23px 46px 80px rgba(0, 0, 0, 0.07);

  cursor: pointer;
  user-select: none;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  border-radius: 0px;
`;

const BackgroundGradient = styled(GradientOverlay)`
  background: linear-gradient(
    to top,
    hsl(0deg 0% 0%),
    hsl(0deg 0% 100%)
  );
`;

const ColorGradient = styled(GradientOverlay)`
  mix-blend-mode: overlay;
  background: linear-gradient(
    to right,
    hsl(var(--hue) 100% 50% / 0),
    hsl(var(--hue) 100% 50% / 1)
  );
`;

const Indicator = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 24px;
  height: 24px;
  border: 2px solid black;
  border-radius: 50%;
  /* prettier-ignore */
  transform: translate(
    calc(-50% + var(--x)),
    calc(-50% + var(--y))
  );
  transform-origin: center center;
  cursor: grab;
  user-select: none;
  box-shadow: inset 0px 0px 0px 2px white;

  &:active {
    cursor: none;
  }

  &:focus {
    outline-offset: 4px;
  }
`;

const Axis = styled.div`
  --height: 3rem;
  position: absolute;
  height: var(--height);
  display: flex;
  justify-content: center;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 25%;
    height: 1px;
    margin: auto;
    background: var(--color-gray-300);
  }

  &::before {
    left: 0;
  }
  &::after {
    right: 0;
  }

  span {
    line-height: var(--height);
    font-size: 0.875rem;
    text-transform: uppercase;
    color: var(--color-gray-600);
    font-weight: var(--font-weight-medium);
    user-select: none;
  }
`;

const SaturationAxis = styled(Axis)`
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(-100%);
`;

const LightnessAxis = styled(Axis)`
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  transform: rotate(-90deg) translate(-100%, -100%);
  transform-origin: top left;
`;

export default SatLitGrid;
